import {HttpClient} from '@angular/common/http';
import {FuseNavigationService} from '@fuse/components/navigation/navigation.service';
import {ApiListingQueryModel} from '@models/@base/api-listing-query.model';
import {ApiPagingQueryModel} from '@models/@base/api-paging-query.model';
import {BasicOverviewModel} from '@models/api/person/basic-overview.model';
import {LegalBasicOverviewModel} from '@models/api/person/legal/legal-basic-overview.model';
import {PhysicalBasicOverviewModel} from '@models/api/person/physical/physical-basic-overview.model';
import {PersonTypeEnum} from '@models/enums/person-type.enum';
import {ApiLegalPersonService} from '@services/api/api-legal-person.service';
import {ApiPersonService} from '@services/api/api-person.service';
import {ApiPhysicalPersonService} from '@services/api/api-physical-person.service';
import {UserInfoModule} from 'app/core/userInfo.module';
import {locale as countries} from 'app/i18n/cs';
import {environment} from 'environments/environment';
import {forkJoin, Observable} from 'rxjs';
import {flatMap} from 'rxjs/operators';

export default class Utils {

    public static isJsonString(str: any): boolean {
        try {
            JSON.parse(String(str));
            return true;
        } catch (e) {
            return false;
        }
    }

    /**
     * Cleans NULLs from Object
     * @param src
     */
    public static cleanNulls<T>(src: T): T {
        if (!(src instanceof Object) || Array.isArray(src)) {
            return src;
        }

        const cpy: T = Object.create({});
        Object.keys(src).forEach((key) => {
            if (src[key] === null || src[key] === undefined) {
                return;
            }

            if (Array.isArray(src[key])) {
                cpy[key] = src[key].map(v => Utils.cleanNulls(v));
            } else if (src[key] instanceof Object) {
                cpy[key] = Utils.cleanNulls(src[key]);
            } else {
                cpy[key] = src[key];
            }
        });

        return cpy;
    }

    /**
     * Formats number to readable byte number
     * @param bytes
     */
    public static readableBytes(bytes: number): string {
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const recalc = ((bytes / Math.pow(1024, i))).toFixed(2);
        return `${recalc} ${sizes[i]}`;
    }

    /**
     * Hide person links (no Client loaded)
     * @param fuseNavigationService
     */
    public static hideClientLinks(fuseNavigationService: FuseNavigationService): void {
        fuseNavigationService.updateNavigationItem('client-links', {hidden: true, translate: `---`});
        fuseNavigationService.updateNavigationItem('client-financni-analyza', {
            type: 'item',
            children: null
        });
        fuseNavigationService.updateNavigationItem('service-introduction', {
            type: 'item',
            children: null
        });
        fuseNavigationService.updateNavigationItem('client-state-and-goals', {
            type: 'item',
            children: null,
            hidden: !UserInfoModule.isFeatureAvailable('client-state-and-goals')
        });
    }

    /**
     * Update person links, adds clientId and changes person name (Client loaded)
     * @param fuseNavigationService
     * @param personService
     * @param physicalPersonService
     * @param legalPersonService
     * @param personType
     * @param clientId
     */
    public static updateClientLinks(
        fuseNavigationService: FuseNavigationService,
        personService: ApiPersonService,
        physicalPersonService: ApiPhysicalPersonService,
        legalPersonService: ApiLegalPersonService,
        personType: PersonTypeEnum,
        clientId: number): void {
        if (PersonTypeEnum.LegalPerson === personType) {
            forkJoin([personService.getBasicOverview(clientId), legalPersonService.getBasicOverview(clientId)])
                .subscribe(
                    data => {
                        Utils.updateClientLinksName(fuseNavigationService, data[0], Utils.getPersonName(null, null, data[1]));
                    }
                );
        } else {
            forkJoin([personService.getBasicOverview(clientId), physicalPersonService.getBasicOverview(clientId)])
                .subscribe(
                    data => {
                        Utils.updateClientLinksName(fuseNavigationService, data[0], Utils.getPersonName(null, data[1], null));
                    }
                );
        }
    }

    private static updateClientLinksName(fuseNavigationService: FuseNavigationService, client: BasicOverviewModel, clientName: string): void {
        fuseNavigationService.updateNavigationItem('client-detail', {url: `/client-detail/${client.id}`});
        fuseNavigationService.updateNavigationItem('client-documents', {url: `/client-documents/${client.id}`});
        fuseNavigationService.updateNavigationItem('client-portal', {url: `/client-portal/${client.id}`, hidden: !client.portalAllowed && !client.isRegisterPortal});
        // fuseNavigationService.updateNavigationItem('client-financial-balance', {url: `/client-financial-balance/${clientId}`});
        // fuseNavigationService.updateNavigationItem('client-financial-balance-v2', {url: `/financni-bilance-v2/${clientId}`});
        fuseNavigationService.updateNavigationItem('service-introduction', {url: `/service-introduction/${client.id}`});
        fuseNavigationService.updateNavigationItem('client-state-and-goals', {
            url: `/client-state-and-goals/${client.id}`,
            hidden: !UserInfoModule.isFeatureAvailable('client-state-and-goals')
        });
        fuseNavigationService.updateNavigationItem('client-financial-plan', {url: `/client-financial-plan/${client.id}`});
        fuseNavigationService.updateNavigationItem('client-tools', {url: `/client-tools/${client.id}`});
        fuseNavigationService.updateNavigationItem('client-goals', {url: `/client-goals/${client.id}`});
        fuseNavigationService.updateNavigationItem('client-links', {hidden: false, translate: `${clientName}`});

        fuseNavigationService.updateNavigationItem('client-financni-analyza', {url: `/financni-analyza/${client.id}`});
        const menuChildren = [
            {
                id: 'client-financni-analyza-2',
                title: 'Finanční bilance',
                type: 'item',
                url: `/financni-analyza/${client.id}/financni-bilance`
            },
            {
                id: 'client-financni-analyza-3',
                title: 'Časová osa',
                type: 'item',
                url: `/financni-analyza/${client.id}/casova-osa`
            },
            {
                id: 'client-financni-analyza-4',
                title: 'Ochrana příjmu',
                type: 'item',
                url: `/financni-analyza/${client.id}/ochrana-prijmu`
            },
            {
                id: 'client-financni-analyza-5',
                title: 'Vlastní bydlení',
                type: 'item',
                url: `/financni-analyza/${client.id}/vlastni-bydleni`
            },
            {
                id: 'client-financni-analyza-6',
                title: 'Budoucnost a renta',
                type: 'item',
                url: `/financni-analyza/${client.id}/budoucnost-a-renta`
            },
            {
                id: 'client-financni-analyza-7',
                title: 'Ochrana majetku',
                type: 'item',
                url: `/financni-analyza/${client.id}/ochrana-majetku`
            },
            {
                id: 'client-financni-analyza-8',
                title: 'Graf FS',
                type: 'item',
                url: `/financni-analyza/${client.id}/graf-fs`
            },
            {
                id: 'client-financni-analyza-9',
                title: 'Závěr',
                type: 'item',
                url: `/financni-analyza/${client.id}/zaver`
            }
        ];

        fuseNavigationService.updateNavigationItem('client-financni-analyza', {
            type: 'collapsable',
            children: menuChildren
        });
    }

    /**
     * Get name from person, Legal or Physical
     * @param person
     * @param physicalPerson
     * @param legalPerson
     * @param physicalPerson
     * @param legalPerson
     */
    public static getPersonName(person: BasicOverviewModel, physicalPerson: PhysicalBasicOverviewModel, legalPerson: LegalBasicOverviewModel): string {
        let name = '';
        if (legalPerson) {
            if (legalPerson.representativeDegreePrefix) {
                name = `${legalPerson.representativeDegreePrefix} `;
            }
            name = `${name}${legalPerson.representativeFirstName} ${legalPerson.representativeSurname}`;
            if (legalPerson.representativeDegreeSuffix) {
                name = `${name}${legalPerson.representativeDegreeSuffix}`;
            }
            name = `${name}\n(${legalPerson.companyName})`;
            return name;
        }
        if (physicalPerson) {
            if (physicalPerson.degreePrefix) {
                name = `${physicalPerson.degreePrefix} `;
            }
            name = `${name}${physicalPerson.firstName} ${physicalPerson.surname}`;
            if (physicalPerson.degreeSuffix) {
                name = `${name} ${physicalPerson.degreeSuffix}`;
            }
            return name;
        }

        return `${person.firstName} ${person.surname}`;
    }

    /**
     * Loads countries from locale
     *
     * {code: '', country: ''}
     */
    public static getCountries(): any[] {
        return Object.entries(countries.data.CODE.COUNTRY).sort((a, b) => {
            if (a[0] === 'CZ' || b[0] === 'CZ') {
                return a[0] === 'CZ' ? -1 : 1;
            }

            if (a[0] === 'SK' || b[0] === 'SK') {
                if (a[0] === 'CZ' || b[0] === 'CZ') {
                    return a[0] === 'SK' ? 1 : -1;
                } else {
                    return a[0] === 'SK' ? -1 : 1;
                }
            }

            return a[1].localeCompare(b[1]);
        }).map((value) => {
            return {
                code: value[0],
                country: value[1]
            };
        });
    }

    /**
     * Smaze vsechny mezery a pak si hraje s predvolackou :)
     * @param phoneNumber
     */
    public static cleanPhoneNumber(phoneNumber: string): string {
        if (phoneNumber === null) {
            return '';
        }

        // trim a vnitrni trim :)
        phoneNumber = phoneNumber.trim().replace(RegExp('\ ', 'gi'), '');

        // dve nuly predelame na plusko
        if (phoneNumber.match(RegExp(/^00/))) {
            phoneNumber = phoneNumber.replace(RegExp(/^00/), '+');
        }

        // pokud neni predvolba 420 nebo 421 tak pridame 420
        if (!phoneNumber.match(RegExp('^\\+'))) {
            phoneNumber = `+420${phoneNumber}`;
        }
        return phoneNumber;
    }

    /**
     * Stahne binarni data jako base64
     * @param httpClient
     * @param url
     */
    public static downloadDataAsBase64(httpClient: HttpClient, url: string): Observable<string> {
        return httpClient.get(url, {responseType: 'blob'}).pipe(
            flatMap(blob => {
                return Utils.blobToBase64(blob);
            })
        );
    }

    /**
     * Blob to base64
     * @param blob
     */
    public static blobToBase64(blob: Blob): Observable<any> {
        const fileReader = new FileReader();
        const observable = new Observable(observer => {
            fileReader.onloadend = () => {
                observer.next(fileReader.result);
                observer.complete();
            };
        });
        fileReader.readAsDataURL(blob);
        return observable;
    }

    /**
     * Generates API listing query string
     * @param query
     */
    public static generateListingQuery(query: ApiListingQueryModel): string {
        const queries: string[] = [];
        if (query != null) {
            if (query.searchTerm != null && query.searchTerm.trim().length > 0) {
                queries.push(`SearchTerm=${encodeURIComponent(query.searchTerm)}`);
            }

            if (query.ordering != null) {
                if (query.ordering.by != null && query.ordering.by.trim().length > 0) {
                    queries.push(`Ordering.By=${encodeURIComponent(query.ordering.by)}`);
                    if (query.ordering.direction != null) {
                        queries.push(`Ordering.Direction=${encodeURIComponent(query.ordering.direction)}`);
                    }
                }
            }

            if (query.page != null) {
                queries.push(`Page.Index=${query.page.index}`);
                queries.push(`Page.Size=${query.page.size > 0 ? query.page.size : 25}`);
            }
        }

        return queries.join('&');
    }

    public static isNullOrEmpty(a: any): boolean {
        return a == null || String(a).isEmpty();
    }

    public static tryGetNumber(a: any): number {
        return Utils.isNullOrEmpty(a) ? null : Number(String(a).replace(/[^\d,.-]/g, ''));
    }

    public static sanitizeStringForNumber(value: string | null): string {
        if (Utils.isNullOrEmpty(value)) {
            return '';
        }
        return String(value).replace(/[^\d,.-]/g, '').replace(/,/g, '.');
    }

    public static safeTrim(a: any): string {
        if (Utils.isNullOrEmpty(a)) {
            return '';
        }

        return String(a).trim();
    }

    public static cleanSpaces(a: any): string {
        if (Utils.isNullOrEmpty(a)) {
            return '';
        }

        return String(a).replace(/ /g, '');
    }

    public static getApolloUrl(a: string): string {
        return `${environment.ApolloUrl}/${a}`;
    }

    public static cleanBirthnumber(x: string): string {
        if (!Utils.isNullOrEmpty(x)) {
            return x.cleanSpaces().replace(/[/-]|[^0-9]/g, '');
        }
        return null;
    }

    public static birthNumberTest(x: string, age: number = null) {
        if (!age) {
            age = 0;
        }

        x = x.cleanSpaces().replace(/[/-]/g, '');
        if (x.length === 0) {
            return true;
        }
        if (x.length < 9) {
            return false;
        }
        let year = parseInt(x.substr(0, 2), 10);
        let month = parseInt(x.substr(2, 2), 10);
        const day = parseInt(x.substr(4, 2), 10);
        const ext = parseInt(x.substr(6, 3), 10);
        if ((x.length === 9) && (year < 54)) {
            return true;
        }
        let c = 0;
        if (x.length === 10) {
            c = parseInt(x.substr(9, 1));
        }
        let m = parseInt(x.substr(0, 9)) % 11;
        if (m === 10) {
            m = 0;
        }
        if (m !== c) {
            return false;
        }
        year += (year < 54) ? 2000 : 1900;
        if ((month > 70) && (year > 2003)) {
            month -= 70;
        } else if (month > 50) {
            month -= 50;
        } else if ((month > 20) && (year > 2003)) {
            month -= 20;
        }
        const d = new Date();
        if ((year + age) > d.getFullYear()) {
            return false;
        }
        if (month === 0) {
            return false;
        }
        if (month > 12) {
            return false;
        }
        if (day === 0) {
            return false;
        }
        return day <= 31;
    }

    public static generatePagingQuery(query: ApiPagingQueryModel): string {
        const queries: string[] = [];
        if (query != null) {
            if (!Utils.isNullOrEmpty(query.searchTerm)) {
                queries.push(`SearchTerm=${encodeURIComponent(query.searchTerm)}`);
            }

            if (query.ordering != null) {
                if (!Utils.isNullOrEmpty(query.ordering.by)) {
                    queries.push(`Ordering.By=${encodeURIComponent(query.ordering.by)}`);
                    if (query.ordering.direction != null) {
                        queries.push(`Ordering.Direction=${encodeURIComponent(query.ordering.direction)}`);
                    }
                }
            }

            if (query.page != null) {
                queries.push(`Page.Index=${query.page.index}`);
                queries.push(`Page.Size=${query.page.size > 0 ? query.page.size : 25}`);
            }
        }

        return queries.join('&');
    }
}
